// Libs
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Controller,
  useForm,
  useFieldArray,
  SubmitHandler,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { v4 as uuidv4 } from "uuid";
import XMarkIcon from "@heroicons/react/24/solid/XMarkIcon";
import TrashIcon from "@heroicons/react/24/solid/TrashIcon";
import { Fragment } from "react";
import { Combobox, Transition } from "@headlessui/react";
import CheckIcon from "@heroicons/react/20/solid/CheckIcon";
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from "react-places-autocomplete";

// Local
import BaseModal from "../BaseModal";
import BaseButtonPrimary from "../BaseButtonPrimary";
import BaseButtonSecondary from "../BaseButtonSecondary";
import BaseInputText from "../BaseInputText";
import BaseInputNumber from "../BaseInputNumber";
import BaseInputDate from "../BaseInputDate";
import BaseInputTextArea from "../BaseInputTextArea";
import BaseInputSelect from "../BaseInputSelect";
import { ExistingVendor } from "../../models/vendor";
import {
  POStatus,
  PODeliveryLocation,
  OPODeliveryLocation,
  PODeliveryLocationValues,
} from "../../models/purchase-order";
import { ExistingSiteKeyLocation } from "../../models/site-key-location";
import { ExistingCraftRecord } from "../../models/craft-record";
import {
  ExistingCustomerLocation,
  getReadableLocationAddress,
} from "../../models/customer-location";
import { ExistingPriceBookItem } from "../../models/price-book-item";
import { DbRead, DbWrite } from "../../database";
import { useAuthStore } from "../../store/firebase-auth";
import { createToastMessageID } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import currencyFormatter from "../../currency";
import * as strings from "../../strings";
import { logger as devLogger } from "../../logging";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { StyledTooltip } from "../../components/StyledTooltip";
import VendorDialog from "../vendors/VendorDialog";
import { VendorFormValues } from "../vendors/VendorForm";
import { Timestamp } from "firebase/firestore";
import { useTypesenseStore } from "../../store/typesense";
import { typesensePriceBookItemsQuery } from "../../utils/typesenseQueries";
import { useSiteKeyDocStore } from "../../store/site-key-doc";

interface Props {
  className: string;
  handleSave: (formValues: PurchaseOrderFormValues) => Promise<void>;
  setAddPurchaseOrderFormOpen: React.Dispatch<React.SetStateAction<boolean>>;
  siteKey: string;
  vendorList: ExistingVendor[];
  craftRecordID?: string | null;
}

// Schema for the form validation
const LineItemSchema = z.object({
  id: z.string(),
  title: z.string().min(1, { message: "Item name is required" }),
  description: z.string().optional().nullable(),
  quantity: z
    .number()
    .min(0.01, { message: "Quantity must be greater than 0" }),
  unitPrice: z.number().min(0, { message: "Unit price can't be negative" }),
  subTotal: z.number(),
  priceBookItemID: z.string().optional().nullable(),
  vendorPartNumber: z.string().nullable().optional(),
  quantityReceived: z.number().default(0),
});

const PurchaseOrderFormSchema = z.object({
  vendorID: z.string().min(1, { message: "Vendor is required" }),
  vendorReferenceNumber: z.string().nullable().optional(),
  deliveryAddressLine1: z
    .string()
    .min(0)
    .max(200, { message: "Address line 1 must be less than 200 characters" })
    .nullable(),
  deliveryAddressLine2: z
    .string()
    .min(0)
    .max(200, { message: "Address line 2 must be less than 200 characters" })
    .nullable(),
  deliveryCity: z
    .string()
    .min(0)
    .max(200, { message: "City must be less than 200 characters" })
    .nullable(),
  deliveryState: z
    .string()
    .min(0)
    .max(200, { message: "State must be less than 200 characters" })
    .nullable(),
  deliveryZipCode: z
    .string()
    .min(0)
    .max(200, { message: "Zip code must be less than 200 characters" })
    .nullable(),
  deliveryCountry: z
    .string()
    .min(0)
    .max(200, { message: "Country must be less than 200 characters" })
    .nullable(),
  issueDate: z.date(),
  expectedDeliveryDate: z.date().optional().nullable(),
  paymentTerms: z.string().optional().nullable(),
  notes: z.string().optional(),
  internalNotes: z.string().optional().nullable(),
  totalTaxAmount: z
    .number()
    .min(0, { message: "Tax amount can't be negative" })
    .default(0),
  status: z.nativeEnum(POStatus),
  lineItems: z.array(LineItemSchema).min(0),
  deliveryLocation: z.nativeEnum(PODeliveryLocation),
});

// Type for the form values
type PurchaseOrderFormState = z.infer<typeof PurchaseOrderFormSchema>;

// Type for the values to return
export type PurchaseOrderFormValues = PurchaseOrderFormState & {
  subTotal: number;
  totalAmount: number;
  locationID: string;
  priceBookItems?: string[];
  customData: Record<string, any>;
  craftRecordID: string | null;
};

// New enhanced PurchaseOrderLineItem component
interface LineItemProps {
  index: number;
  field: Record<string, any>;
  control: any;
  errors: any;
  currency: string;
  onRemove: () => void;
  watch: any;
  setValue: any;
  siteKey: string;
  locationID: string | null;
}

function PurchaseOrderLineItem({
  index,
  field,
  control,
  errors,
  currency,
  onRemove,
  watch,
  setValue,
  siteKey,
  locationID,
}: LineItemProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [priceBookItems, setPriceBookItems] = useState<ExistingPriceBookItem[]>(
    [],
  );
  const [selectedPBItem, setSelectedPBItem] =
    useState<ExistingPriceBookItem | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCustomItem, setIsCustomItem] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const typesenseSearchKey = useTypesenseStore(
    (state) => state.scopedSearchKey,
  );
  const siteKeyDoc = useSiteKeyDocStore((state) => state.siteKeyDoc);

  // Check if this line item already has data (editing existing item)
  const currentTitle = watch(`lineItems.${index}.title`);
  const currentPriceBookItemID = watch(`lineItems.${index}.priceBookItemID`);

  useEffect(() => {
    // If the item already has data, expand it
    if (currentTitle || currentPriceBookItemID) {
      setIsExpanded(true);
      if (!currentPriceBookItemID) {
        setIsCustomItem(true);
      }
    }
  }, [currentTitle, currentPriceBookItemID]);

  // Auto-expand if there are validation errors
  useEffect(() => {
    if (
      errors.lineItems?.[index]?.title ||
      errors.lineItems?.[index]?.quantity ||
      errors.lineItems?.[index]?.unitPrice
    ) {
      setIsExpanded(true);
    }
  }, [errors.lineItems, index]);

  // Search pricebook items using Typesense
  const searchPriceBookItems = useCallback(
    async (searchTerm: string) => {
      if (!typesenseSearchKey || !searchTerm.trim()) {
        setPriceBookItems([]);
        return;
      }

      setIsSearching(true);
      try {
        const pbItems = await typesensePriceBookItemsQuery(
          typesenseSearchKey,
          searchTerm,
        );

        // Filter by location if site has that setting enabled
        if (
          siteKeyDoc?.customizations.filterPriceBookItemsByLocationID ===
            true &&
          locationID
        ) {
          const filtered = pbItems.filter(
            (item) =>
              item.locationID === locationID || item.locationID === null,
          );
          setPriceBookItems(filtered);
        } else {
          setPriceBookItems(pbItems);
        }
      } catch (error) {
        console.error("Error searching price book items:", error);
        setPriceBookItems([]);
      } finally {
        setIsSearching(false);
      }
    },
    [typesenseSearchKey, siteKeyDoc, locationID],
  );

  // Handle search input change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    searchPriceBookItems(value);
  };

  // Handle selecting a pricebook item
  const handleSelectPriceBookItem = (pbItem: ExistingPriceBookItem) => {
    setSelectedPBItem(pbItem);
    setIsExpanded(true);
    setIsCustomItem(false);
    setSearchQuery("");
    setPriceBookItems([]);

    // Use cost if available, otherwise fall back to unitPrice
    const unitPrice =
      pbItem.cost !== null && pbItem.cost !== undefined
        ? pbItem.cost
        : pbItem.unitPrice;

    // Populate form fields with pricebook item data
    setValue(`lineItems.${index}.title`, pbItem.title);
    setValue(`lineItems.${index}.description`, pbItem.description || "");
    setValue(`lineItems.${index}.unitPrice`, unitPrice);
    setValue(`lineItems.${index}.priceBookItemID`, pbItem.id);
    setValue(`lineItems.${index}.quantity`, 1);
    setValue(`lineItems.${index}.subTotal`, unitPrice * 1);
  };

  // Handle confirming custom item
  const handleConfirmCustomItem = () => {
    if (!searchQuery.trim()) return;

    setIsExpanded(true);
    setIsCustomItem(true);
    setPriceBookItems([]);

    // Set the title and clear pricebook item ID
    setValue(`lineItems.${index}.title`, searchQuery.trim());
    setValue(`lineItems.${index}.priceBookItemID`, null);
    setValue(`lineItems.${index}.quantity`, 1);
    setValue(`lineItems.${index}.unitPrice`, 0);
    setValue(`lineItems.${index}.subTotal`, 0);
    setSearchQuery("");
  };

  // If not expanded, show search interface
  if (!isExpanded) {
    return (
      <div className="space-y-3">
        {/* Show validation errors even in collapsed state */}
        {(errors.lineItems?.[index]?.title ||
          errors.lineItems?.[index]?.quantity ||
          errors.lineItems?.[index]?.unitPrice) && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3">
            <div className="text-sm text-red-700">
              <div className="mb-1 font-medium">Please fix these issues:</div>
              <ul className="space-y-1">
                {errors.lineItems?.[index]?.title && (
                  <li>• {errors.lineItems[index]?.title?.message}</li>
                )}
                {errors.lineItems?.[index]?.quantity && (
                  <li>• {errors.lineItems[index]?.quantity?.message}</li>
                )}
                {errors.lineItems?.[index]?.unitPrice && (
                  <li>• {errors.lineItems[index]?.unitPrice?.message}</li>
                )}
              </ul>
              <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className="mt-2 inline-flex items-center text-sm font-medium text-red-700 hover:text-red-800"
              >
                Click here to edit item details →
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex-1">
            <Combobox
              value={selectedPBItem}
              onChange={handleSelectPriceBookItem}
            >
              <div className="relative">
                <Combobox.Input
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Start typing to search items or enter custom item name..."
                  value={searchQuery}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      searchQuery.trim() &&
                      priceBookItems.length === 0
                    ) {
                      event.preventDefault();
                      handleConfirmCustomItem();
                    }
                  }}
                />

                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                    {isSearching ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                        Searching...
                      </div>
                    ) : priceBookItems.length === 0 && searchQuery.trim() ? (
                      <div className="relative cursor-default select-none px-4 py-2">
                        <div className="text-gray-700">No items found.</div>
                        <button
                          type="button"
                          onClick={handleConfirmCustomItem}
                          className="mt-2 w-full rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
                        >
                          Add "{searchQuery}" as custom item
                        </button>
                      </div>
                    ) : (
                      <>
                        {priceBookItems.map((pbItem) => (
                          <Combobox.Option
                            key={pbItem.id}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                active
                                  ? "bg-blue-600 text-white"
                                  : "text-gray-900"
                              }`
                            }
                            value={pbItem}
                          >
                            {({ selected, active }) => (
                              <>
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col">
                                    <span
                                      className={`block truncate ${selected ? "font-medium" : "font-normal"}`}
                                    >
                                      {pbItem.title}
                                    </span>
                                    {pbItem.description && (
                                      <span className="block truncate text-sm text-gray-500">
                                        {pbItem.description}
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-sm">
                                    {currencyFormatter(
                                      pbItem.unitPrice,
                                      currency,
                                    )}
                                    /{pbItem.units}
                                  </div>
                                </div>
                                {selected ? (
                                  <span
                                    className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                      active ? "text-white" : "text-blue-600"
                                    }`}
                                  >
                                    <CheckIcon
                                      className="h-5 w-5"
                                      aria-hidden="true"
                                    />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                        {/* Add custom item option when there are price book results and search query */}
                        {priceBookItems.length > 0 && searchQuery.trim() && (
                          <>
                            <div className="my-1 border-t border-gray-200"></div>
                            <div className="relative cursor-default select-none px-4 py-2">
                              <button
                                type="button"
                                onClick={handleConfirmCustomItem}
                                className="w-full rounded bg-green-600 px-3 py-1 text-sm text-white hover:bg-green-700"
                              >
                                Add "{searchQuery}" as custom item
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </Combobox.Options>
                </Transition>
              </div>
            </Combobox>
          </div>

          <button
            type="button"
            onClick={onRemove}
            className="ml-3 p-2 text-red-500 hover:text-red-700"
            aria-label="Remove item"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  // Expanded view with all fields
  return (
    <div className="space-y-3">
      {/* Header with item info and back button */}
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {isCustomItem ? "(Custom Item)" : "(From Price Book)"}
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-2 text-red-500 hover:text-red-700"
          aria-label="Remove item"
        >
          <TrashIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Line 1: Item name, quantity, unit price, subtotal */}
      <div className="grid grid-cols-[2fr,1fr,1fr,1fr] gap-3">
        {/* Item Name */}
        <div>
          <Controller
            name={`lineItems.${index}.title`}
            control={control}
            render={({ field }) => (
              <BaseInputText
                text="Item Name"
                inputName={`lineItems.${index}.title`}
                admin={true}
                required={true}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                disabled={!isCustomItem} // Disable if from pricebook
              />
            )}
          />
          {errors.lineItems?.[index]?.title && (
            <div className="mt-1 text-xs text-red-500">
              {errors.lineItems[index]?.title?.message}
            </div>
          )}
        </div>

        {/* Quantity */}
        <div>
          <Controller
            name={`lineItems.${index}.quantity`}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <BaseInputNumber
                text="Qty"
                inputName={`lineItems.${index}.quantity`}
                admin={true}
                required={true}
                value={rest.value === 0 ? "" : rest.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string or valid numbers
                  if (value === "" || value === "0") {
                    rest.onChange(0);
                  } else {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      rest.onChange(numValue);
                    }
                  }
                  // Update subtotal immediately
                  const unitPrice = watch(`lineItems.${index}.unitPrice`) || 0;
                  const quantity = value === "" ? 0 : parseFloat(value) || 0;
                  setValue(`lineItems.${index}.subTotal`, quantity * unitPrice);
                }}
              />
            )}
          />
          {errors.lineItems?.[index]?.quantity && (
            <div className="mt-1 text-xs text-red-500">
              {errors.lineItems[index]?.quantity?.message}
            </div>
          )}
        </div>

        {/* Unit Price */}
        <div>
          <Controller
            name={`lineItems.${index}.unitPrice`}
            control={control}
            render={({ field: { ref, ...rest } }) => (
              <BaseInputNumber
                text="Unit Price"
                inputName={`lineItems.${index}.unitPrice`}
                admin={true}
                required={true}
                value={rest.value === 0 ? "" : rest.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  const value = event.target.value;
                  // Allow empty string or valid numbers
                  if (value === "" || value === "0") {
                    rest.onChange(0);
                  } else {
                    const numValue = parseFloat(value);
                    if (!isNaN(numValue)) {
                      rest.onChange(numValue);
                    }
                  }
                  // Update subtotal immediately
                  const quantity = watch(`lineItems.${index}.quantity`) || 0;
                  const unitPrice = value === "" ? 0 : parseFloat(value) || 0;
                  setValue(`lineItems.${index}.subTotal`, quantity * unitPrice);
                }}
              />
            )}
          />
          {errors.lineItems?.[index]?.unitPrice && (
            <div className="mt-1 text-xs text-red-500">
              {errors.lineItems[index]?.unitPrice?.message}
            </div>
          )}
        </div>

        {/* Subtotal (plain text) */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Subtotal
          </label>
          <div className="pt-2 text-sm font-medium text-gray-900">
            {currencyFormatter(
              watch(`lineItems.${index}.subTotal`) || 0,
              currency,
            )}
          </div>
        </div>
      </div>

      {/* Line 2: Description, part number */}
      <div className="grid grid-cols-[2fr,1fr] gap-3">
        {/* Description */}
        <div>
          <Controller
            name={`lineItems.${index}.description`}
            control={control}
            render={({ field }) => (
              <BaseInputTextArea
                text="Description (Optional)"
                inputName={`lineItems.${index}.description`}
                admin={true}
                required={false}
                rows={2}
                value={field.value || ""}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        {/* Vendor Part Number */}
        <div>
          <Controller
            name={`lineItems.${index}.vendorPartNumber`}
            control={control}
            render={({ field }) => (
              <BaseInputText
                text="Part #"
                inputName={`lineItems.${index}.vendorPartNumber`}
                admin={true}
                required={false}
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default function AddNewPurchaseOrderForm({
  handleSave,
  setAddPurchaseOrderFormOpen,
  siteKey,
  vendorList,
  craftRecordID,
  ...props
}: Props) {
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [selectedVendor, setSelectedVendor] = useState<ExistingVendor | null>(
    null,
  );
  const [siteLocations, setSiteLocations] = useState<ExistingSiteKeyLocation[]>(
    [],
  );
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [isEditingAddress, setIsEditingAddress] = useState<boolean>(false);
  const [addVendorDialogOpen, setAddVendorDialogOpen] = useState(false);

  // Address state for vendor creation
  const [address, setAddress] = useState<string>("");
  const [geocoderResult, setGeocoderResult] =
    useState<google.maps.GeocoderResult>({} as google.maps.GeocoderResult);
  const [showManualEntryForm, setShowManualEntryForm] =
    useState<boolean>(false);
  const [displayAddressError, setDisplayAddressError] =
    useState<boolean>(false);

  // Delivery location state
  const [craftRecord, setCraftRecord] = useState<ExistingCraftRecord | null>(
    null,
  );
  const [customerLocation, setCustomerLocation] =
    useState<ExistingCustomerLocation | null>(null);
  const [deliveryLocationLoading, setDeliveryLocationLoading] = useState(false);

  const addToastMessage = useToastMessageStore(
    (state) => state.addToastMessage,
  );
  const firebaseUser = useAuthStore((state) => state.firebaseUser);

  // Get site locations
  useEffect(() => {
    async function fetchLocations() {
      try {
        const locations = await DbRead.siteKeyLocations.getAll(siteKey);
        setSiteLocations(locations);
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    }

    fetchLocations();
  }, [siteKey]);

  const purchaseOrderFormDefaultValues = useMemo(() => {
    const defaultLineItem = {
      id: uuidv4(),
      title: "",
      description: null,
      quantity: 1,
      unitPrice: 0,
      subTotal: 0,
      priceBookItemID: null,
      vendorPartNumber: null,
      quantityReceived: 0,
    };

    return {
      vendorID: "",
      vendorReferenceNumber: null,
      deliveryAddressLine1: "",
      deliveryAddressLine2: null,
      deliveryCity: "",
      deliveryState: "",
      deliveryZipCode: "",
      deliveryCountry: "USA",
      issueDate: new Date(),
      expectedDeliveryDate: null,
      paymentTerms: "Net 30",
      notes: "",
      internalNotes: null,
      totalTaxAmount: 0,
      status: POStatus.DRAFT,
      lineItems: [defaultLineItem],
      deliveryLocation: PODeliveryLocation.OFFICE,
    };
  }, []);

  const {
    control,
    formState: { errors, isSubmitting },
    reset,
    handleSubmit,
    watch,
    setValue,
  } = useForm<PurchaseOrderFormState>({
    defaultValues: purchaseOrderFormDefaultValues,
    resolver: zodResolver(PurchaseOrderFormSchema),
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "lineItems",
  });

  const watchedLineItems = watch("lineItems");
  const watchedTaxAmount = watch("totalTaxAmount");
  const watchVendorID = watch("vendorID");
  const watchDeliveryLocation = watch("deliveryLocation");

  // Update selected vendor when vendorID changes
  useEffect(() => {
    if (watchVendorID) {
      const vendor = vendorList.find((v) => v.id === watchVendorID);
      setSelectedVendor(vendor || null);
    } else {
      setSelectedVendor(null);
    }
  }, [watchVendorID, vendorList]);

  // Fetch craft record when linked to job
  useEffect(() => {
    async function fetchCraftRecord() {
      if (!craftRecordID) {
        setCraftRecord(null);
        return;
      }

      try {
        const record = await DbRead.parentRecords.get(siteKey, craftRecordID);
        setCraftRecord(record);
      } catch (error) {
        console.error("Error fetching craft record:", error);
        setCraftRecord(null);
      }
    }

    fetchCraftRecord();
  }, [craftRecordID, siteKey]);

  // Fetch customer location when delivery location is set to Job Address
  useEffect(() => {
    async function fetchCustomerLocation() {
      if (
        watchDeliveryLocation !== PODeliveryLocation.JOB_ADDRESS ||
        !craftRecord?.customerLocationID
      ) {
        setCustomerLocation(null);
        return;
      }

      setDeliveryLocationLoading(true);
      try {
        const location = await DbRead.customerLocations.getSingle(
          siteKey,
          craftRecord.customerLocationID,
        );
        setCustomerLocation(location);

        // Update delivery address fields with customer location
        if (location) {
          setValue("deliveryAddressLine1", location.addressLine1 || "");
          setValue("deliveryAddressLine2", location.addressLine2 || null);
          setValue("deliveryCity", location.city || "");
          setValue("deliveryState", location.state || "");
          setValue("deliveryZipCode", location.zipCode || "");
          setValue("deliveryCountry", location.county || "USA"); // Note: using county field
        }
      } catch (error) {
        console.error("Error fetching customer location:", error);
        setCustomerLocation(null);
        addToastMessage({
          id: createToastMessageID(),
          message: "Error loading job address",
          dialog: false,
          type: "error",
        });
      } finally {
        setDeliveryLocationLoading(false);
      }
    }

    fetchCustomerLocation();
  }, [watchDeliveryLocation, craftRecord, siteKey, setValue, addToastMessage]);

  // Handle delivery location change
  const handleDeliveryLocationChange = (location: PODeliveryLocation) => {
    setValue("deliveryLocation", location);

    // Clear address fields when changing delivery location
    setValue("deliveryAddressLine1", "");
    setValue("deliveryAddressLine2", null);
    setValue("deliveryCity", "");
    setValue("deliveryState", "");
    setValue("deliveryZipCode", "");
    setValue("deliveryCountry", "USA");

    // Populate address based on delivery location
    if (location === PODeliveryLocation.OFFICE) {
      // Use the selected location or first site location
      const locationToUse = selectedLocationId
        ? siteLocations.find((loc) => loc.id === selectedLocationId)
        : siteLocations[0];

      if (locationToUse) {
        setValue("deliveryAddressLine1", locationToUse.addressLine1 || "");
        setValue("deliveryAddressLine2", locationToUse.addressLine2 || null);
        setValue("deliveryCity", locationToUse.city || "");
        setValue("deliveryState", locationToUse.state || "");
        setValue("deliveryZipCode", locationToUse.zipCode || "");
        setValue("deliveryCountry", locationToUse.country || "USA");
      }
    } else if (location === PODeliveryLocation.VENDOR && selectedVendor) {
      // Use vendor address
      setValue("deliveryAddressLine1", selectedVendor.addressLine1 || "");
      setValue("deliveryAddressLine2", selectedVendor.addressLine2 || null);
      setValue("deliveryCity", selectedVendor.city || "");
      setValue("deliveryState", selectedVendor.state || "");
      setValue("deliveryZipCode", selectedVendor.zipCode || "");
      setValue("deliveryCountry", selectedVendor.country || "USA");
    }
    // For JOB_ADDRESS, the address will be populated by the useEffect that fetches customer location
    // For OTHER, leave fields empty for manual entry
  };

  // Calculate subtotals for line items
  useEffect(() => {
    if (!watchedLineItems) return;

    // Update the subtotals for each line item to ensure they're accurate
    watchedLineItems.forEach((item, index) => {
      const calculatedSubTotal = (item.quantity || 0) * (item.unitPrice || 0);
      if (calculatedSubTotal !== item.subTotal) {
        setValue(`lineItems.${index}.subTotal`, calculatedSubTotal);
      }
    });
  }, [watchedLineItems, setValue]);

  useEffect(() => {
    reset(purchaseOrderFormDefaultValues);
  }, [purchaseOrderFormDefaultValues, reset]);

  // Ensure issue date is always today
  useEffect(() => {
    setValue("issueDate", new Date());
  }, [setValue]);

  // Handle location change
  const handleLocationChange = (locationId: string) => {
    setSelectedLocationId(locationId);

    // Find the selected location
    const selectedLocation = siteLocations.find(
      (location) => location.id === locationId,
    );

    if (selectedLocation) {
      // If delivery location is set to Office, update the delivery address fields
      if (watchDeliveryLocation === PODeliveryLocation.OFFICE) {
        setValue("deliveryAddressLine1", selectedLocation.addressLine1 || "");
        setValue("deliveryAddressLine2", selectedLocation.addressLine2 || null);
        setValue("deliveryCity", selectedLocation.city || "");
        setValue("deliveryState", selectedLocation.state || "");
        setValue("deliveryZipCode", selectedLocation.zipCode || "");
        setValue("deliveryCountry", selectedLocation.country || "USA");
      }
    }
  };

  /* fn that handle all the states that needs to be reset when the dialog is closed */
  function closeAndReset() {
    reset();
    setDisplayError(false);
    setAddPurchaseOrderFormOpen(false);
  }

  // Handler for saving new vendor
  const handleSaveNewVendor = async (formValues: VendorFormValues) => {
    try {
      if (!firebaseUser) throw Error("No Firebase user was found");

      await DbWrite.vendors.create({
        ...formValues,
        siteKey: siteKey,
        createdBy: firebaseUser.uid,
        lastModifiedBy: firebaseUser.uid,
      });

      return Promise.resolve();
    } catch (error) {
      console.error("Error creating vendor:", error);
      return Promise.reject(error);
    }
  };

  // Address handling functions for vendor dialog
  async function handleSelectAddress(selectedAddress: string) {
    const results = await geocodeByAddress(selectedAddress);
    setGeocoderResult(results[0]);
    setAddress(selectedAddress);
    setDisplayAddressError(false);
  }

  function resetAddressState() {
    setAddress("");
    setGeocoderResult({} as google.maps.GeocoderResult);
    setShowManualEntryForm(false);
    setDisplayAddressError(false);
  }

  function getAddressFieldComponent() {
    return (
      <div className="flex w-full items-center gap-4">
        <PlacesAutocomplete
          value={address}
          onChange={setAddress}
          onSelect={handleSelectAddress}
        >
          {({
            getInputProps,
            suggestions,
            getSuggestionItemProps,
            loading,
          }) => (
            <div className="w-full">
              <BaseInputText
                {...getInputProps({
                  admin: true,
                  required: true,
                  inputName: "fullAddress",
                  text: "Address",
                })}
              />
              <div
                className={
                  suggestions.length > 0 || loading
                    ? "absolute z-10 max-w-sm rounded border bg-white p-2"
                    : ""
                }
              >
                {loading && <div className="p-2 text-primary">Loading...</div>}
                {suggestions.map((suggestion, suggestionIdx) => {
                  const style = {
                    backgroundColor: suggestion.active
                      ? "var(--primary-opacity-90)"
                      : "#fff",
                    borderRadius: "0.25rem",
                    padding: "5px",
                  };
                  return (
                    <p
                      {...getSuggestionItemProps(suggestion, { style })}
                      key={suggestionIdx}
                    >
                      {suggestion.description}
                    </p>
                  );
                })}
              </div>
            </div>
          )}
        </PlacesAutocomplete>
      </div>
    );
  }

  function addLineItem() {
    append({
      id: uuidv4(),
      title: "",
      description: null,
      quantity: 1,
      unitPrice: 0,
      subTotal: 0,
      priceBookItemID: null,
      vendorPartNumber: null,
      quantityReceived: 0,
    });
  }

  const onSubmit: SubmitHandler<PurchaseOrderFormState> = async (
    formValues,
  ) => {
    console.log("formValues", formValues);

    if (!selectedVendor) {
      console.log("No vendor selected - showing warning");
      addToastMessage({
        id: createToastMessageID(),
        message: "Please select a vendor",
        dialog: false,
        type: "warning",
      });
      return;
    }

    // Check if location is selected when there are multiple locations
    if (!selectedLocationId) {
      console.log("No location selected - showing warning");
      addToastMessage({
        id: createToastMessageID(),
        message: "Please select a location",
        dialog: false,
        type: "warning",
      });
      return;
    }

    // Check if line items exist
    console.log("lineItems count:", formValues.lineItems.length);
    if (formValues.lineItems.length === 0) {
      console.log("No line items - showing warning");
      addToastMessage({
        id: createToastMessageID(),
        message: "Please add at least one item",
        dialog: false,
        type: "warning",
      });
      return;
    }

    // Calculate totals
    const subTotal = formValues.lineItems.reduce(
      (acc, item) => acc + item.subTotal,
      0,
    );
    const totalTaxAmount = formValues.totalTaxAmount;
    const totalDiscount = 0;
    const totalAmount = subTotal + totalTaxAmount;

    // Use the selected location ID
    const locationID =
      selectedLocationId ||
      (siteLocations.length > 0 ? siteLocations[0].id : "");

    // Extract price book item IDs if any
    const priceBookItems = formValues.lineItems
      .filter((item) => item.priceBookItemID)
      .map((item) => item.priceBookItemID as string);

    const purchaseOrderValues: PurchaseOrderFormValues = {
      ...formValues,
      subTotal,
      totalAmount,
      totalTaxAmount,
      locationID,
      customData: {},
      priceBookItems: priceBookItems.length > 0 ? priceBookItems : undefined,
      craftRecordID: craftRecordID || null,
    };

    try {
      console.log("Calculated values:", {
        subTotal,
        totalAmount,
        totalTaxAmount,
      });
      console.log("About to call handleSave with:", purchaseOrderValues);
      await handleSave(purchaseOrderValues);

      console.log("handleSave completed successfully");
      // Show success message
      addToastMessage({
        id: createToastMessageID(),
        message: "Purchase order created successfully",
        dialog: false,
        type: "success",
      });

      closeAndReset();
    } catch (error) {
      console.error("Error in handleSave:", error);
      devLogger.error(error);

      // Show error toast message
      addToastMessage({
        id: createToastMessageID(),
        message:
          strings.ERR_CREATE_NEW_PURCHASE_ORDER ||
          "Failed to create purchase order",
        dialog: false,
        type: "error",
      });
    }
  };

  // Calculate subtotal and total amount
  const lineItems = watch("lineItems") || [];
  const subTotal = lineItems.reduce(
    (total, item) => total + (item.subTotal || 0),
    0,
  );
  const taxAmount = watch("totalTaxAmount") || 0;
  const totalOrderAmount = subTotal + taxAmount;

  // Function to format money values
  const formatMoney = (amount: number) => {
    return currencyFormatter(amount, "USD");
  };

  return (
    <div>
      <form
        onSubmit={(e) => {
          console.log("Form submission event triggered");
          console.log("Current form state:", {
            errors: Object.keys(errors).length > 0,
            isSubmitting,
            errorDetails: errors,
          });

          // Manually log field values for debugging
          const currentFormValues = watch();
          console.log("Current form values:", currentFormValues);

          handleSubmit((data) => {
            console.log(
              "Form validation passed, calling onSubmit with data:",
              data,
            );
            onSubmit(data);
          })(e);
        }}
        className={`${props.className} w-full`}
        autoComplete="off"
      >
        {/* Main Grid Layout: 2 columns on large screens */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Left Column (2/3 width on large screens) */}
          <div className="space-y-6 lg:col-span-2">
            {/* Purchase Order Information Section */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-800">
                Purchase Order Information
              </h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Left column - Vendor info */}
                <div className="space-y-4">
                  {/* Vendor Selection */}
                  <div>
                    {/* Location Selector - Only show if there are multiple locations */}
                    {siteLocations.length > 1 && (
                      <div>
                        <BaseInputSelect
                          admin={true}
                          text="Select Location"
                          required={true}
                          id="locationSelector"
                          name="locationSelector"
                          value={selectedLocationId}
                          onChange={(e) => handleLocationChange(e.target.value)}
                          disabled={isEditingAddress}
                          className="mb-4"
                        >
                          <option value="" disabled>
                            Select a location
                          </option>
                          {siteLocations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.title} - {location.department}
                            </option>
                          ))}
                        </BaseInputSelect>
                      </div>
                    )}

                    <Controller
                      name="vendorID"
                      control={control}
                      render={({ field }) => (
                        <BaseInputSelect
                          admin={true}
                          text="Vendor"
                          id="vendorID"
                          name={field.name}
                          onBlur={field.onBlur}
                          onChange={(e) => {
                            field.onChange(e);
                            // If delivery location is set to Vendor, update the delivery address
                            if (
                              watchDeliveryLocation ===
                              PODeliveryLocation.VENDOR
                            ) {
                              const selectedVendor = vendorList.find(
                                (v) => v.id === e.target.value,
                              );
                              if (selectedVendor) {
                                setValue(
                                  "deliveryAddressLine1",
                                  selectedVendor.addressLine1 || "",
                                );
                                setValue(
                                  "deliveryAddressLine2",
                                  selectedVendor.addressLine2 || null,
                                );
                                setValue(
                                  "deliveryCity",
                                  selectedVendor.city || "",
                                );
                                setValue(
                                  "deliveryState",
                                  selectedVendor.state || "",
                                );
                                setValue(
                                  "deliveryZipCode",
                                  selectedVendor.zipCode || "",
                                );
                                setValue(
                                  "deliveryCountry",
                                  selectedVendor.country || "USA",
                                );
                              }
                            }
                          }}
                          ref={field.ref}
                          value={field.value}
                          required
                        >
                          <option value="">Select Vendor</option>
                          {vendorList.map((vendor) => (
                            <option key={vendor.id} value={vendor.id}>
                              {vendor.name}
                            </option>
                          ))}
                        </BaseInputSelect>
                      )}
                    />
                    {errors.vendorID && (
                      <div className="mt-1 text-sm text-red-500">
                        {errors.vendorID.message}
                      </div>
                    )}

                    {/* Selected Vendor Info Card */}
                    {selectedVendor && (
                      <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                        <div className="flex items-center space-x-3">
                          {selectedVendor.logoURL && (
                            <img
                              src={selectedVendor.logoURL}
                              alt={`${selectedVendor.name} logo`}
                              className="h-12 w-12 rounded-md border border-gray-200 object-cover"
                            />
                          )}
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">
                              {selectedVendor.name}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {selectedVendor.addressLine1},{" "}
                              {selectedVendor.city}, {selectedVendor.state}
                            </p>
                            {selectedVendor.email &&
                              selectedVendor.email.length > 0 && (
                                <p className="text-sm text-gray-600">
                                  {selectedVendor.email[0]}
                                </p>
                              )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* New Vendor Button */}
                    <div className="mt-2">
                      <BaseButtonSecondary
                        type="button"
                        onClick={() => setAddVendorDialogOpen(true)}
                        className="px-3 py-1 text-sm"
                      >
                        + Add New Vendor
                      </BaseButtonSecondary>
                    </div>
                  </div>
                </div>

                {/* Right column - PO and Dates */}
                <div className="space-y-4">
                  {/* Payment Terms */}
                  <div>
                    <Controller
                      name="paymentTerms"
                      control={control}
                      render={({ field }) => (
                        <BaseInputText
                          admin={true}
                          text="Payment Terms"
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          ref={field.ref}
                          value={field.value || ""}
                          id="paymentTerms"
                          name={field.name}
                          type="text"
                        />
                      )}
                    />
                  </div>

                  {/* Vendor Reference # */}
                  <div>
                    <Controller
                      name="vendorReferenceNumber"
                      control={control}
                      render={({ field }) => (
                        <BaseInputText
                          admin={true}
                          text="Vendor Reference #"
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          ref={field.ref}
                          value={field.value || ""}
                          id="vendorReferenceNumber"
                          name={field.name}
                          type="text"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              {/* Notes Section - Stays the same */}
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h3 className="text-md mb-3 font-medium text-gray-700">
                  Notes
                </h3>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <div>
                    <Controller
                      name="notes"
                      control={control}
                      render={({ field }) => (
                        <BaseInputTextArea
                          admin={true}
                          text="Notes for Vendor"
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          ref={field.ref}
                          value={field.value || ""}
                          id="notes"
                          name={field.name}
                          rows={3}
                        />
                      )}
                    />
                  </div>

                  <div>
                    <Controller
                      name="internalNotes"
                      control={control}
                      render={({ field }) => (
                        <BaseInputTextArea
                          admin={true}
                          text="Internal Notes (not visible to vendor)"
                          onBlur={field.onBlur}
                          onChange={field.onChange}
                          ref={field.ref}
                          value={field.value || ""}
                          id="internalNotes"
                          name={field.name}
                          rows={3}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Line Items Section */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <div className="mb-4 flex items-center justify-between border-b pb-2">
                <h2 className="text-lg font-semibold text-gray-800">
                  Line Items
                </h2>
                <BaseButtonSecondary
                  className="px-3 py-1 text-sm"
                  onClick={addLineItem}
                  type="button"
                >
                  Add Item
                </BaseButtonSecondary>
              </div>

              {/* No items message */}
              {fields.length === 0 && (
                <div className="mb-4 rounded bg-gray-50 p-4 text-center text-gray-500">
                  No items added. Click "Add Item" to add parts to this purchase
                  order.
                </div>
              )}

              {/* Line Items List */}
              <div className="space-y-3">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="rounded border border-gray-200 bg-gray-50 p-3"
                  >
                    <PurchaseOrderLineItem
                      index={index}
                      field={field}
                      control={control}
                      errors={errors}
                      currency="USD"
                      onRemove={() => remove(index)}
                      watch={watch}
                      setValue={setValue}
                      siteKey={siteKey}
                      locationID={selectedLocationId || null}
                    />
                  </div>
                ))}
              </div>

              {/* Add another item button at the bottom if there are already items */}
              {fields.length > 0 && (
                <div className="mt-4 flex justify-center">
                  <BaseButtonSecondary
                    onClick={addLineItem}
                    type="button"
                    className="px-4 py-2"
                  >
                    + Add Another Item
                  </BaseButtonSecondary>
                </div>
              )}
            </div>
          </div>

          {/* Right Column (1/3 width on large screens) */}
          <div className="flex flex-col space-y-6">
            {/* Shipping Method */}
            <div className="rounded-lg bg-white p-6 shadow-md">
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-800">
                Shipping Information
              </h2>

              {/* Delivery Address Section */}
              <div className="mt-6">
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Delivery Location
                  </label>
                  <Controller
                    name="deliveryLocation"
                    control={control}
                    render={({ field }) => (
                      <BaseInputSelect
                        admin={true}
                        text=""
                        id="deliveryLocation"
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (
                            value &&
                            Object.values(PODeliveryLocation).includes(
                              value as PODeliveryLocation,
                            )
                          ) {
                            const deliveryLocation =
                              value as PODeliveryLocation;
                            field.onChange(deliveryLocation);
                            handleDeliveryLocationChange(deliveryLocation);
                          } else {
                            // Handle empty string case - don't trigger delivery location change
                            field.onChange("");
                          }
                        }}
                        value={field.value || ""}
                        disabled={deliveryLocationLoading}
                      >
                        {Object.values(OPODeliveryLocation).map((location) => (
                          <option
                            key={location}
                            value={location}
                            disabled={
                              location === PODeliveryLocation.JOB_ADDRESS &&
                              !craftRecordID
                            }
                          >
                            {location}
                            {location === PODeliveryLocation.JOB_ADDRESS &&
                              !craftRecordID &&
                              " (No job linked)"}
                          </option>
                        ))}
                      </BaseInputSelect>
                    )}
                  />
                  {errors.deliveryLocation && (
                    <span className="text-red-600">
                      {errors.deliveryLocation.message}
                    </span>
                  )}
                </div>

                {/* Address Preview - Show for Office, Vendor, and manually entered addresses */}
                {(watchDeliveryLocation === PODeliveryLocation.OFFICE ||
                  watchDeliveryLocation === PODeliveryLocation.VENDOR ||
                  watchDeliveryLocation === PODeliveryLocation.OTHER) && (
                  <div className="mb-4 rounded-md bg-gray-50 p-3">
                    <p className="mb-2 text-sm font-medium text-gray-700">
                      Delivery Address:
                    </p>
                    <div className="text-sm text-gray-600">
                      {watch("deliveryAddressLine1") && (
                        <p>{watch("deliveryAddressLine1")}</p>
                      )}
                      {watch("deliveryAddressLine2") && (
                        <p>{watch("deliveryAddressLine2")}</p>
                      )}
                      {(watch("deliveryCity") ||
                        watch("deliveryState") ||
                        watch("deliveryZipCode")) && (
                        <p>
                          {watch("deliveryCity")}
                          {watch("deliveryCity") && ", "}
                          {watch("deliveryState")} {watch("deliveryZipCode")}
                        </p>
                      )}
                      {watch("deliveryCountry") &&
                        watch("deliveryCountry") !== "USA" && (
                          <p>{watch("deliveryCountry")}</p>
                        )}
                      {!watch("deliveryAddressLine1") &&
                        watchDeliveryLocation === PODeliveryLocation.OTHER && (
                          <p className="italic text-gray-400">
                            Enter address details below
                          </p>
                        )}
                    </div>
                  </div>
                )}

                {watchDeliveryLocation === PODeliveryLocation.JOB_ADDRESS &&
                  customerLocation && (
                    <div className="mb-4 rounded-md bg-blue-50 p-3">
                      <p className="mb-2 text-sm font-medium text-blue-700">
                        Job Address:
                      </p>
                      <div className="text-sm text-gray-600">
                        {customerLocation.locationName && (
                          <p className="font-medium">
                            {customerLocation.locationName}
                          </p>
                        )}
                        <p>{getReadableLocationAddress(customerLocation)}</p>
                        {customerLocation.zipCode && (
                          <p>{customerLocation.zipCode}</p>
                        )}
                      </div>
                    </div>
                  )}

                {/* Edit Address Button - Hide for Job Address */}
                {watchDeliveryLocation !== PODeliveryLocation.JOB_ADDRESS && (
                  <button
                    type="button"
                    onClick={() => setIsEditingAddress(!isEditingAddress)}
                    className="mb-3 inline-flex items-center rounded-md border border-blue-300 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {isEditingAddress ? "Hide Address Fields" : "Edit Address"}
                  </button>
                )}

                {/* Address Editing Fields - Show when editing or when "Other" is selected */}
                {(isEditingAddress ||
                  watchDeliveryLocation === PODeliveryLocation.OTHER) && (
                  <div className="mt-4 space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                    <div>
                      <Controller
                        name="deliveryAddressLine1"
                        control={control}
                        rules={{ required: "Address line 1 is required" }}
                        render={({ field }) => (
                          <BaseInputText
                            admin={true}
                            text="Address Line 1"
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            ref={field.ref}
                            value={field.value || ""}
                            id="deliveryAddressLine1"
                            name={field.name}
                            type="text"
                          />
                        )}
                      />
                      {errors.deliveryAddressLine1 && (
                        <div className="mt-1 text-sm text-red-500">
                          {errors.deliveryAddressLine1.message}
                        </div>
                      )}
                    </div>

                    <div>
                      <Controller
                        name="deliveryAddressLine2"
                        control={control}
                        render={({ field }) => (
                          <BaseInputText
                            admin={true}
                            text="Address Line 2"
                            onBlur={field.onBlur}
                            onChange={field.onChange}
                            ref={field.ref}
                            value={field.value || ""}
                            id="deliveryAddressLine2"
                            name={field.name}
                            type="text"
                          />
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Controller
                          name="deliveryCity"
                          control={control}
                          render={({ field }) => (
                            <BaseInputText
                              admin={true}
                              text="City"
                              onBlur={field.onBlur}
                              onChange={field.onChange}
                              ref={field.ref}
                              value={field.value || ""}
                              id="deliveryCity"
                              name={field.name}
                              type="text"
                            />
                          )}
                        />
                        {errors.deliveryCity && (
                          <div className="mt-1 text-sm text-red-500">
                            {errors.deliveryCity.message}
                          </div>
                        )}
                      </div>

                      <div>
                        <Controller
                          name="deliveryState"
                          control={control}
                          render={({ field }) => (
                            <BaseInputText
                              admin={true}
                              text="State"
                              onBlur={field.onBlur}
                              onChange={field.onChange}
                              ref={field.ref}
                              value={field.value || ""}
                              id="deliveryState"
                              name={field.name}
                              type="text"
                            />
                          )}
                        />
                        {errors.deliveryState && (
                          <div className="mt-1 text-sm text-red-500">
                            {errors.deliveryState.message}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Controller
                          name="deliveryZipCode"
                          control={control}
                          render={({ field }) => (
                            <BaseInputText
                              admin={true}
                              text="ZIP Code"
                              onBlur={field.onBlur}
                              onChange={field.onChange}
                              ref={field.ref}
                              value={field.value || ""}
                              id="deliveryZipCode"
                              name={field.name}
                              type="text"
                            />
                          )}
                        />
                        {errors.deliveryZipCode && (
                          <div className="mt-1 text-sm text-red-500">
                            {errors.deliveryZipCode.message}
                          </div>
                        )}
                      </div>

                      <div>
                        <Controller
                          name="deliveryCountry"
                          control={control}
                          render={({ field }) => (
                            <BaseInputText
                              admin={true}
                              text="Country"
                              onBlur={field.onBlur}
                              onChange={field.onChange}
                              ref={field.ref}
                              value={field.value || "USA"}
                              id="deliveryCountry"
                              name={field.name}
                              type="text"
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Expected Delivery Date */}
              <div className="mt-4">
                <Controller
                  name="expectedDeliveryDate"
                  control={control}
                  render={({ field }) => {
                    const date =
                      field.value instanceof Date ? field.value : null;
                    return (
                      <BaseInputDate
                        inputName="expectedDeliveryDate"
                        displayText="Expected Delivery Date"
                        value={date ? date.toISOString() : null}
                        onChange={(selectedDate) => {
                          field.onChange(selectedDate);
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>

            {/* Spacer to push the totals to the bottom */}
            <div className="flex-grow"></div>

            {/* Order Totals Section */}
            <div className="mt-auto rounded-lg bg-white p-6 shadow-md">
              <h2 className="mb-4 border-b pb-2 text-lg font-semibold text-gray-800">
                Order Totals
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">{formatMoney(subTotal)}</span>
                </div>

                <div>
                  <Controller
                    name="totalTaxAmount"
                    control={control}
                    render={({ field }) => (
                      <BaseInputNumber
                        text="Tax Amount"
                        inputName="totalTaxAmount"
                        admin={true}
                        required={false}
                        value={field.value}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => {
                          const numberValue = event.target.valueAsNumber;
                          field.onChange(isNaN(numberValue) ? "" : numberValue);
                        }}
                      />
                    )}
                  />
                  {errors.totalTaxAmount && (
                    <div className="mt-1 text-sm text-red-500">
                      {errors.totalTaxAmount.message}
                    </div>
                  )}
                </div>

                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <span className="text-lg font-semibold text-gray-700">
                    Total:
                  </span>
                  <span className="text-lg font-bold">
                    {formatMoney(totalOrderAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end space-x-4 border-t border-gray-200 pt-6">
          <BaseButtonSecondary
            type="button"
            onClick={() => closeAndReset()}
            className="px-6"
          >
            Cancel
          </BaseButtonSecondary>

          <BaseButtonPrimary
            type="submit"
            formNoValidate
            disabled={isSubmitting}
            isBusy={isSubmitting}
            busyText={strings.buttons.BUSY_SAVING}
            className="px-6"
          >
            {strings.buttons.SAVE}
          </BaseButtonPrimary>
        </div>
      </form>

      {/* Add New Vendor Dialog */}
      <VendorDialog
        isDialogOpen={addVendorDialogOpen}
        geocoderResult={geocoderResult}
        showManualEntryForm={showManualEntryForm}
        setShowManualEntryForm={setShowManualEntryForm}
        closeDialog={() => {
          setAddVendorDialogOpen(false);
          resetAddressState();
        }}
        handleSave={handleSaveNewVendor}
      >
        {{
          AddressField: getAddressFieldComponent(),
        }}
      </VendorDialog>
    </div>
  );
}
